import { hostBridge } from '@publicala/delfino';
import type {
    EpubR2Locations,
    EpubR2Selection,
    Note,
    NoteCreatePayload,
    NoteDeletePayload,
    NoteListPayload,
    NoteUpdatePayload,
    Position,
    ShareLinkResponse,
} from '@publicala/delfino';
import {
    DEFAULT_NOTES_LIST_MOCK,
    DEFAULT_SHARE_LINK_MOCK,
    createMockNote,
    createMockUpdatedNote,
    isE2EMockActive,
} from '../e2eMockGuard';

/**
 * @R2 Internal R2 location structure from highlightModel (CSS selector-based).
 * Contains the full Readium Locator data for navigation.
 */
interface VolpeR2Location {
    /** CSS selector-based selection data. */
    selection?: EpubR2Selection;
    /** @R2 Resource href from the publication readingOrder. */
    href?: string;
    /** @R2 MIME type of the resource (e.g., "application/xhtml+xml"). */
    type?: string;
    /** @R2 Readium Locator locations object for navigation. */
    locations?: EpubR2Locations;
    [key: string]: unknown;
}

/**
 * Volpe note payload with flat position fields.
 * Transforms to NoteCreatePayload with nested position before sending to host.
 */
type VolpeNotePayload = Omit<NoteCreatePayload, 'position' | 'colorSlug'> &
    Partial<Omit<Position, 'type'>> & {
        /** @R2 R2 location with CSS selector-based selection (from highlightModel). */
        location?: VolpeR2Location | null;
        /** Color slug reference (for custom colors). */
        colorSlug?: string | null;
    };

/**
 * Notes API for Volpe.
 * Provides methods to create, update, delete, and list notes via hostBridge.
 */
class NotesAPI {
    /**
     * Create a note.
     * Returns mock data in E2E standalone mode.
     *
     * @param payload - Note data with flat position fields
     * @returns The created note with assigned ID
     */
    async store(payload: VolpeNotePayload): Promise<Note> {
        const position = this.buildPosition(payload);

        if (isE2EMockActive()) {
            return createMockNote(payload, position);
        }

        const createPayload: NoteCreatePayload = {
            uuid: payload.uuid,
            color: payload.color,
            colorSlug: payload.colorSlug,
            totalText: payload.totalText,
            totalLength: payload.totalLength,
            annotationText: payload.annotationText,
            annotationFormat: payload.annotationFormat,
            position,
        };

        return hostBridge.notes.store(createPayload);
    }

    /**
     * Update an existing note.
     * Returns mock data in E2E standalone mode.
     *
     * @param noteId - ID of the note to update
     * @param data - Data to update
     * @returns The updated note
     */
    async update(noteId: string, data: NoteUpdatePayload['data']): Promise<Note> {
        if (isE2EMockActive()) {
            return createMockUpdatedNote(noteId, data);
        }

        const updatePayload: NoteUpdatePayload = {
            noteId,
            data,
        };

        return hostBridge.notes.update(updatePayload);
    }

    /**
     * Delete a note.
     * Skipped in E2E standalone mode.
     *
     * @param noteId - ID of the note to delete
     */
    async delete(noteId: string): Promise<void> {
        if (isE2EMockActive()) {
            return;
        }

        const deletePayload: NoteDeletePayload = {
            noteId,
        };

        return hostBridge.notes.delete(deletePayload);
    }

    /**
     * List all notes.
     * Returns mock data in E2E standalone mode.
     *
     * @param issueId - Optional filter by issue ID
     * @returns List of notes
     */
    async list(issueId?: string): Promise<Note[]> {
        if (isE2EMockActive()) {
            return DEFAULT_NOTES_LIST_MOCK;
        }

        const listPayload: NoteListPayload = {
            issueId,
        };

        return hostBridge.notes.list(listPayload);
    }

    /**
     * Creates or retrieves a share link for a color tag in a book.
     * Idempotent: returns the same URL for the same combination.
     * Returns mock data in E2E standalone mode.
     *
     * @param colorSlug - Color slug to share (matches the slug stored in user.reader_settings.customColors)
     * @param issueId - Issue (book) ID. Numeric to match Farfalla's Issue.id (unsigned bigint).
     * @returns Share link response with the URL
     */
    async createShareLink(colorSlug: string, issueId: number): Promise<ShareLinkResponse> {
        if (isE2EMockActive()) {
            return DEFAULT_SHARE_LINK_MOCK;
        }

        return hostBridge.notes.createShareLink({ colorSlug, issueId });
    }

    /**
     * Build position object from flat payload fields.
     *
     * @param payload - Payload with flat position fields
     * @returns Position object for PDF, EPUB R1, or EPUB R2
     * @throws Error if position fields are missing
     */
    private buildPosition(payload: VolpeNotePayload): Position {
        // @R2 EPUB: CSS selector-based positioning (Readium 2)
        if (payload.location?.selection) {
            return {
                type: 'epub',
                epubChapter: payload.epubChapter,
                epubR2Location: {
                    startCss: payload.location.selection.startCss,
                    endCss: payload.location.selection.endCss,
                    startElementOffset: payload.location.selection.startElementOffset,
                    startOffset: payload.location.selection.startOffset,
                    endOffset: payload.location.selection.endOffset,
                    text: payload.location.selection.text,
                    href: payload.location.href,
                    type: payload.location.type,
                    locations: payload.location.locations,
                },
            };
        }

        // R1 EPUB: CFI/offset-based positioning (legacy)
        if (payload.epubLocationStartCfi) {
            return {
                type: 'epub',
                epubLocationStartCfi: payload.epubLocationStartCfi,
                epubLocationEndCfi: payload.epubLocationEndCfi,
                epubChapter: payload.epubChapter,
                epubLocationStartChar: payload.epubLocationStartChar,
                epubLocation: payload.epubLocation,
            };
        }

        if (payload.startPage !== undefined) {
            return {
                type: 'pdf',
                startPage: payload.startPage,
                startPageStartOffset: payload.startPageStartOffset,
                startPageLength: payload.startPageLength,
                endPage: payload.endPage,
                endPageLength: payload.endPageLength,
            };
        }

        throw new Error('Invalid note position: missing EPUB R2, EPUB R1, and PDF position fields');
    }
}

export const notesAPI = new NotesAPI();
